import Vue from "vue";
import { NovelClient } from "@/lib/clients";
import { Novel, NovelSetting, NovelGroupSummary, ManuscriptSetting, TCopyNovelPayload } from "@/lib/models";
import { ActionContext, ActionTree, GetterTree, MutationTree, Store } from "vuex";
import {
  db,
  ManuscriptSettings as ManuscriptSettingsDexie,
  GeneralSettings as GeneralSettingsDexie,
} from "@/lib/indexeddb";
import {
  genre as genreSettings,
  category as categorySettings,
  workStatus as workStatusSettings,
} from "@/lib/novelSettings";
import { NovelGroup } from "@/lib/models/novelGroup";

const novelClient = new NovelClient();

// Stateの型定義
export interface NovelsState {
  novels: Novel[];
  genres: NovelSetting[];
  categories: NovelSetting[];
  workStatuses: NovelSetting[];
  manuscriptSettings: ManuscriptSetting[];
  expandsSideMenu: boolean;
  novelContentLength: number;
  novelListSortKey: string;
  novelListSortOrder: "asc" | "desc";
  novelListLayout: "grid" | "list";
  filterGroups: NovelGroupSummary[];
}

type Getters = {
  novel(state: NovelsState): (id: string) => Novel | undefined;
  novels(state: NovelsState): Novel[];
  genres(state: NovelsState): NovelSetting[];
  categories(state: NovelsState): NovelSetting[];
  workStatuses(state: NovelsState): NovelSetting[];
  manuscriptSetting(state: NovelsState): (novelId: string) => ManuscriptSetting;
  expandsSideMenu(state: NovelsState): boolean;
  novelContentLength(state: NovelsState): number;
  novelListSortKey(state: NovelsState): string;
  novelListSortOrder(state: NovelsState): "asc" | "desc";
  novelListLayout(state: NovelsState): "grid" | "list";
  filterGroups(state: NovelsState, getters: any, rootState: any, rootGetters: any): NovelGroupSummary[];
  isScriptMode(state: NovelsState): (novelId: string) => boolean;
};

const getters: GetterTree<NovelsState, {}> & Getters = {
  novel: (state) => (id: string) => state.novels.find((novel) => novel.novelId === id),
  novels: (state) => state.novels,
  genres: (state) => state.genres,
  categories: (state) => state.categories,
  workStatuses: (state) => state.workStatuses,
  manuscriptSetting: (state) => (novelId) => {
    const initialManuscriptSetting: ManuscriptSetting = {
      novelId,
      wordCountOnVertical: 20,
      lineCountPerPage: 20,
      fontSizeRate: 1,
    };
    const manuscriptSetting = state.manuscriptSettings.find(
      (manuscriptSetting) => manuscriptSetting.novelId === novelId
    );
    return manuscriptSetting || initialManuscriptSetting;
  },
  expandsSideMenu: (state) => state.expandsSideMenu,
  novelContentLength: (state) => state.novelContentLength,
  novelListSortKey: (state) => state.novelListSortKey,
  novelListSortOrder: (state) => state.novelListSortOrder,
  novelListLayout: (state) => state.novelListLayout,
  filterGroups: (state, _getters, _rootState, rootGetters) => {
    const existingGroups = rootGetters["novelGroupModule/groups"] as NovelGroup[];
    const existingIds = new Set(existingGroups.map((g) => g.groupId));
    return state.filterGroups.filter((fg) => existingIds.has(fg.groupId));
  },
  isScriptMode: (state) => (novelId: string) =>
    state.novels.find((n) => n.novelId === novelId)?.manuscriptMode === "script",
};

const mutations: MutationTree<NovelsState> = {
  createNovel(state, payload) {
    return state.novels.push(payload);
  },
  setNovels(state, payload) {
    return (state.novels = payload);
  },
  setGenres(state, payload: NovelSetting[]) {
    return (state.genres = payload);
  },
  setCategories(state, payload: NovelSetting[]) {
    return (state.categories = payload);
  },
  setWorkStatuses(state, payload: NovelSetting[]) {
    return (state.workStatuses = payload);
  },
  setManuscriptSetting(state, payload: ManuscriptSetting[]) {
    return (state.manuscriptSettings = payload);
  },
  updateManuscriptSetting(state, payload: ManuscriptSetting) {
    const isIncludeManuscriptSetting = state.manuscriptSettings.some(
      (manuscriptSetting) => manuscriptSetting.novelId === payload.novelId
    );
    if (isIncludeManuscriptSetting) {
      state.manuscriptSettings = state.manuscriptSettings.map((manuscriptSetting) => {
        if (manuscriptSetting.novelId === payload.novelId) {
          return payload;
        }
        return manuscriptSetting;
      });
    } else {
      state.manuscriptSettings.push(payload);
    }
    return state.manuscriptSettings;
  },
  setExpandsSideMenu(state, payload: boolean) {
    return (state.expandsSideMenu = payload);
  },
  setNovelContentLength(state, payload: number) {
    return (state.novelContentLength = payload);
  },
  setNovelListSortKey(state, payload: string) {
    return (state.novelListSortKey = payload);
  },
  setNovelListSortOrder(state, payload: "asc" | "desc") {
    return (state.novelListSortOrder = payload);
  },
  setNovelListLayout(state, payload: "grid" | "list") {
    return (state.novelListLayout = payload);
  },
  setFilterGroups(state, payload: NovelGroupSummary[]) {
    return (state.filterGroups = payload);
  },
};

type FetchNovelPayload = {
  novelId: string;
};

type CreateNovelPayload = {
  title: string;
  genre?: string;
  category?: string[];
  wordLength?: number;
  isSkip?: boolean;
  manuscriptMode?: "default" | "script";
};

type UpdateNovelPayload = Partial<Novel> & {
  novelId: string;
};

type DeleteNovelPayload = {
  novelId: string;
  title: string;
};

type Actions = {
  /**
   * Fetch
   */
  fetchNovel(this: Store<{}>, injectee: ActionContext<NovelsState, {}>, payload: FetchNovelPayload): Promise<Novel>;

  /**
   * Create
   */

  createNovel(this: Store<{}>, injectee: ActionContext<NovelsState, {}>, payload: CreateNovelPayload): Promise<Novel>;

  /**
   * Update
   */

  updateNovel(this: Store<{}>, injectee: ActionContext<NovelsState, {}>, payload: UpdateNovelPayload): Promise<Novel>;

  /**
   * Delete
   */

  deleteNovel(this: Store<{}>, injectee: ActionContext<NovelsState, {}>, payload: DeleteNovelPayload): Promise<void>;

  /**
   * Copy
   */

  copyNovel(this: Store<{}>, injectee: ActionContext<NovelsState, {}>, payload: TCopyNovelPayload): Promise<boolean>;
};

/**
 * 執筆予定の文字数からGAで計測する文字列を取得する
 * @param wordLength 執筆予定の文字数
 * @returns GAで計測する文字列
 */
const getAnalyticsWordCount = (wordLength: number) => {
  if (wordLength === 0) {
    return "未入力";
  }
  if (wordLength <= 10000) {
    return "~10,000";
  }
  if (wordLength <= 50000) {
    return "10,001 ~ 50,000";
  }
  if (wordLength <= 100000) {
    return "50,001 ~ 100,000";
  }
  return "100,001~";
};

const actions: ActionTree<NovelsState, {}> & Actions = {
  async initialize({ commit }, _) {
    const novels = await novelClient.fetchAllNovel();
    commit("setNovels", novels);
  },

  async fetchNovel({ state, commit }, payload): Promise<Novel> {
    const { novelId } = payload;
    const { novels: preNovels } = state;

    const novel = await novelClient.fetchNovelById(novelId);
    const novels = preNovels.map((preNovel) => {
      if (novel && preNovel.novelId === novel.novelId) {
        return novel;
      }

      return preNovel;
    });

    commit("setNovels", novels);

    return novel;
  },

  async createNovel({ state, commit }, payload): Promise<Novel> {
    const { title, genre, category, wordLength, isSkip, manuscriptMode } = payload;
    const { novels: preNovels } = state;
    const novelId = await novelClient.createNovel(title, genre, category, manuscriptMode);
    const novel = {
      novelId,
      ...payload,
      manuscriptMode: manuscriptMode ?? "default",
      groups: [],
    };
    const novels = [...preNovels, novel];
    commit("setNovels", novels);

    // GA計測
    if (Vue.prototype.$gtm) {
      Vue.prototype.$gtm.trackEvent({
        event: "create_novel",
        word_count: getAnalyticsWordCount(wordLength || 0),
        genre: genre || "",
        // nola-app で送信しているスペース区切りの文字列と合わせる
        categories: category?.length ? category.join(", ") : [],
        skip: isSkip || false,
      });
    }

    return { ...novel };
  },

  async updateNovel({ state, commit }, payload): Promise<Novel> {
    const { novels: preNovels } = state;
    const { novelId } = payload;
    let novel = preNovels.find((preNovel) => preNovel.novelId === novelId);

    if (!novel) {
      throw new Error("Novel not found.");
    }

    const updated = await novelClient.updateNovel({
      ...novel,
      ...payload,
    });
    // updateNovel ミューテーションは入力・レスポンスともに manuscriptMode / groups を含まない
    // （どちらもこのミューテーションでは更新されない）。API 戻り値で丸ごと置き換えると、
    // 保存後に manuscriptMode（＝脚本モード判定 isScriptMode）や groups が失われるため、
    // 更新入力に含められないこれらの項目はローカルの値を保持する。
    novel = {
      ...updated,
      manuscriptMode: novel.manuscriptMode,
      groups: novel.groups,
    };

    const novels = preNovels.map((preNovel) => {
      if (novel && preNovel.novelId === novel.novelId) {
        return novel;
      }

      return preNovel;
    });

    commit("setNovels", novels);

    return novel;
  },

  async updateManuscriptSetting({ commit }, payload: ManuscriptSetting) {
    commit("updateManuscriptSetting", payload);

    const { novelId, wordCountOnVertical, lineCountPerPage, fontSizeRate } = payload;
    await db.transaction("readwrite", db.manuscriptSettings, async () => {
      await db.manuscriptSettings.put(
        new ManuscriptSettingsDexie(novelId, wordCountOnVertical, lineCountPerPage, fontSizeRate)
      );
    });
  },
  async initializeManuscriptSettings({ commit }) {
    const manuscriptSettingsInIndexedDB = await db.transaction(
      "readonly",
      db.manuscriptSettings,
      async () => await db.manuscriptSettings.toArray()
    );
    if (manuscriptSettingsInIndexedDB.length) {
      commit("setManuscriptSetting", manuscriptSettingsInIndexedDB);
    }
  },
  async initializeExpandsSideMenu({ commit }) {
    const expandsSideMenuInIndexedDB = await db.transaction(
      "readonly",
      db.generalSettings,
      async () => await db.generalSettings.get("expandsSideMenu")
    );
    if (expandsSideMenuInIndexedDB) {
      commit("setExpandsSideMenu", expandsSideMenuInIndexedDB.value);
    }
  },
  async toggleSideMenu({ commit, state }) {
    const { expandsSideMenu } = state;

    await db.transaction("readwrite", db.generalSettings, async () => {
      await db.generalSettings.put(new GeneralSettingsDexie(!expandsSideMenu), "expandsSideMenu");
    });

    commit("setExpandsSideMenu", !expandsSideMenu);
  },

  async updateNovelListSortKey({ commit }, payload: string) {
    commit("setNovelListSortKey", payload);
    await db.transaction("readwrite", db.generalSettings, async () => {
      await db.generalSettings.put(new GeneralSettingsDexie(payload), "novelListSortKey");
    });
  },
  async updateNovelListSortOrder({ commit }, payload: "asc" | "desc") {
    commit("setNovelListSortOrder", payload);
    await db.transaction("readwrite", db.generalSettings, async () => {
      await db.generalSettings.put(new GeneralSettingsDexie(payload), "novelListSortOrder");
    });
  },
  async updateNovelListLayout({ commit }, payload: "grid" | "list") {
    commit("setNovelListLayout", payload);
    await db.transaction("readwrite", db.generalSettings, async () => {
      await db.generalSettings.put(new GeneralSettingsDexie(payload), "novelListLayout");
    });
  },
  async updateNovelListFilters(
    { commit },
    payload: {
      genres: NovelSetting[];
      categories: NovelSetting[];
      workStatuses: NovelSetting[];
      groups: NovelGroupSummary[];
    }
  ) {
    commit("setGenres", payload.genres);
    commit("setCategories", payload.categories);
    commit("setWorkStatuses", payload.workStatuses);
    commit("setFilterGroups", payload.groups);

    const filterIds = {
      genres: payload.genres.map((g) => g.id),
      categories: payload.categories.map((c) => c.id),
      workStatuses: payload.workStatuses.map((ws) => ws.id),
      groupIds: payload.groups.map((g) => g.groupId),
    };
    await db.transaction("readwrite", db.generalSettings, async () => {
      await db.generalSettings.put(new GeneralSettingsDexie(filterIds), "novelListFilters");
    });
  },
  async initializeNovelListSettings({ commit, rootGetters }) {
    const sortKeyInDB = await db.transaction(
      "readonly",
      db.generalSettings,
      async () => await db.generalSettings.get("novelListSortKey")
    );
    if (sortKeyInDB && ["createdAt", "updatedAt", "title", ""].includes(sortKeyInDB.value)) {
      commit("setNovelListSortKey", sortKeyInDB.value);
    }

    const sortOrderInDB = await db.transaction(
      "readonly",
      db.generalSettings,
      async () => await db.generalSettings.get("novelListSortOrder")
    );
    if (sortOrderInDB && ["asc", "desc"].includes(sortOrderInDB.value)) {
      commit("setNovelListSortOrder", sortOrderInDB.value);
    }

    const layoutInDB = await db.transaction(
      "readonly",
      db.generalSettings,
      async () => await db.generalSettings.get("novelListLayout")
    );
    if (layoutInDB && ["grid", "list"].includes(layoutInDB.value)) {
      commit("setNovelListLayout", layoutInDB.value);
    }

    const filtersInDB = await db.transaction(
      "readonly",
      db.generalSettings,
      async () => await db.generalSettings.get("novelListFilters")
    );
    if (filtersInDB) {
      const { genres: gIds = [], categories: cIds = [], workStatuses: wsIds = [], groupIds = [] } = filtersInDB.value;
      commit(
        "setGenres",
        genreSettings.filter((g) => gIds.includes(g.id))
      );
      commit(
        "setCategories",
        categorySettings.filter((c) => cIds.includes(c.id))
      );
      commit(
        "setWorkStatuses",
        workStatusSettings.filter((ws) => wsIds.includes(ws.id))
      );
      const allGroups = rootGetters["novelGroupModule/groups"] as NovelGroup[];
      commit(
        "setFilterGroups",
        allGroups.filter((g) => groupIds.includes(g.groupId)).map((g) => ({ groupId: g.groupId, name: g.name }))
      );
    }
  },

  async deleteNovel({ state, commit }, payload): Promise<void> {
    const { novels: preNovels } = state;
    const { novelId, title } = payload;
    await novelClient.deleteNovel(novelId, title);
    const novels = preNovels.filter((preNovel) => preNovel.novelId !== novelId);
    commit("setNovels", novels);
  },

  async copyNovel(_, payload): Promise<boolean> {
    return await novelClient.copyNovel(payload);
  },

  async requestNolaAgentForNovel({ state, commit }, payload): Promise<void> {
    const { novels: preNovels } = state;
    const novel = await novelClient.requestNolaAgentForNovel(payload.novelId, payload.campaignId);
    const novels = preNovels.map((preNovel) => {
      if (novel && preNovel.novelId === novel.novelId) {
        return {
          ...preNovel,
          agentStatus: novel.agentStatus,
          agentStartedAt: novel.agentStartedAt,
          agentCampaignIds: novel.agentCampaignIds,
        };
      }
      return preNovel;
    });
    commit("setNovels", novels);
  },

  async revokeNolaAgentForNovel({ state, commit }, payload): Promise<void> {
    const { novels: preNovels } = state;
    const novel = await novelClient.revokeNolaAgentForNovel(payload.novelId);
    const novels = preNovels.map((preNovel) => {
      if (novel && preNovel.novelId === novel.novelId) {
        return {
          ...preNovel,
          agentStatus: novel.agentStatus,
          agentStartedAt: novel.agentStartedAt,
          agentCampaignIds: novel.agentCampaignIds,
        };
      }
      return preNovel;
    });
    commit("setNovels", novels);
  },
};

export default {
  namespaced: true,
  state: {
    novels: [],
    genres: [],
    categories: [],
    workStatuses: [],
    manuscriptSettings: [],
    expandsSideMenu: true,
    novelContentLength: 0,
    novelListSortKey: "createdAt",
    novelListSortOrder: "asc",
    novelListLayout: "grid",
    filterGroups: [],
  },
  getters,
  actions,
  mutations,
};
